import { useStores } from '@littlebird/sdk';
import { useMemo } from 'react';

import { FEATURES, hasFeature, useFeature } from '@/features/feature-flags';
import { useProjectMembers } from '@/features/projects/project-members.query';
import { userStore } from '@/stores/user.store';

import { type TeamChatMember } from './team-chat-members.store';
import { type TeamChatComposerConfig } from './use-team-chat-composer';

/** Team chats need the `team-chats` flag, and the TypeScript backend that serves their routes */
export const teamChatsEnabled = (): boolean =>
  hasFeature(FEATURES.TEAM_CHATS) && userStore.backendChoice === 'typescript';

export function useTeamChatsEnabled(): boolean {
  useFeature();
  return teamChatsEnabled();
}

/** A chat of a project shared with someone else is a team chat; any other chat has no config */
export function useTeamChatConfig(
  projectId: string | null | undefined,
  chatId: string,
  ownerId: number | null
): TeamChatComposerConfig | undefined {
  const { projects } = useStores();
  const enabled = useTeamChatsEnabled();
  const membersQuery = useProjectMembers(
    projectId ?? '',
    enabled && Boolean(projectId)
  );
  const viewerId = Number(userStore.userId);
  const projectName = projectId ? projects.getById(projectId)?.name : undefined;
  const memberRows = membersQuery.data;
  return useMemo(() => {
    if (!enabled || !projectId || ownerId === null || memberRows === undefined)
      return undefined;
    const members: TeamChatMember[] = memberRows.flatMap((member) =>
      member.userId === null || member.userId === viewerId
        ? []
        : [
            {
              userId: member.userId,
              name: member.displayName ?? member.email ?? 'Someone',
              isOwner: member.userId === ownerId,
            },
          ]
    );
    if (members.length === 0) return undefined;
    return {
      projectId,
      chatId,
      projectName: projectName ?? 'this project',
      members,
      viewerIsOwner: ownerId === viewerId,
    };
  }, [chatId, memberRows, enabled, ownerId, projectId, projectName, viewerId]);
}
